/**
 * Custom Search — standalone panel behavior (full search data layer)
 * --------------------------------------------------------------
 * Shopify-native data sources, used for what each is actually built for:
 *
 *   - /search/suggest.json                 Predictive Search API.
 *                                           Fast, capped at ~10 results.
 *                                           Used ONLY for the small
 *                                           suggestion-pill row.
 *
 *   - /search?...&view=custom-search        Shopify's real full-text
 *                                           search index (the same one
 *                                           behind the default /search
 *                                           results page), reached via
 *                                           an alternate template. This
 *                                           is the source of truth for
 *                                           the CANDIDATE product list:
 *                                           the result grid, real result
 *                                           count, sorting, and pagination.
 *
 *   - /products/<handle>.js                 Guaranteed-native per-product
 *                                           endpoint (no custom template,
 *                                           no alternate view -- the same
 *                                           API used for cart/quick-add
 *                                           everywhere in Shopify themes).
 *                                           Used to fetch each candidate's
 *                                           REAL variant SKUs for exact/
 *                                           partial SKU matching, so SKU
 *                                           search never silently depends
 *                                           on one specific alternate
 *                                           template actually including a
 *                                           "skus" field. See
 *                                           ensureRealSkus / classifySkuMatches.
 *
 *   - /collections/all/products.json        Recommendations fallback
 *                                           (Shopify's contextual
 *                                           recommendations endpoint
 *                                           needs a product_id the
 *                                           search panel doesn't have).
 *
 * Pagination is infinite-scroll: an IntersectionObserver watches a
 * sentinel below the grid and fetches+appends the next page, so a
 * 200+ product result set is never loaded into the browser all at once.
 *
 * Latest Searches persist via localStorage. Popular Searches has no
 * native Shopify data source, so it stays a merchant-editable list
 * (section.settings.popular_searches).
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------ */
  /* Constants                                                     */
  /* ------------------------------------------------------------ */

  var PLACEHOLDER_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f2f2f2'/%3E%3Cg fill='%23cfcfcf'%3E%3Ccircle cx='150' cy='150' r='34'/%3E%3Cpath d='M40 320 L150 200 L230 280 L280 220 L360 320 Z'/%3E%3C/g%3E%3C/svg%3E";

  var SUGGESTION_LIMIT = 6;
  var DEBOUNCE_MS = 300;
  var LATEST_SEARCHES_KEY = 'cs:latest-searches';
  var LATEST_SEARCHES_MAX = 10;
  var RECOMMENDED_LIMIT = 8;
  var SEARCH_VIEW = 'custom-search'; // must match templates/search.custom-search.liquid
  // Upper bound on how many candidates ensureRealSkus() will fetch real
  // variant data for per query. A pure scale/performance safeguard (never a
  // query-type guess): a genuine SKU match has consistently ranked within
  // the first few results in real testing, so this stays comfortably above
  // that while keeping worst-case request volume/latency bounded for very
  // broad single-word queries (e.g. a common brand name with dozens of
  // same-page candidates).
  var SKU_CHECK_CANDIDATE_LIMIT = 24;

  var SORT_MAP = {
    relevance: null,
    'price-asc': 'price-ascending',
    'price-desc': 'price-descending'
  };

  var FALLBACK_POPULAR_SEARCHES = [
    'chlorine tablets', 'pool cover', 'salt cell', 'skimmer net', 'ph reducer', 'filter pump'
  ];

  /* ------------------------------------------------------------ */
  /* Generic helpers                                               */
  /* ------------------------------------------------------------ */

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function normalizeImage(src) {
    if (!src) return null;
    if (src.indexOf('//') === 0) return 'https:' + src;
    return src;
  }

  function shopifyRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = root || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }
  function formatMoney(amount, currencyCode) {
    var num = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(num);
    } catch (e) {
      return '$' + num.toFixed(2);
    }
  }

  function highlightMatch(text, query) {
    var rawText = text == null ? '' : String(text);
    var rawQuery = query == null ? '' : String(query).trim();
    if (!rawQuery) return escapeHtml(rawText);

    var lowerText = rawText.toLocaleLowerCase();
    var lowerQuery = rawQuery.toLocaleLowerCase();
    var index = lowerText.indexOf(lowerQuery);

    if (index === -1) return escapeHtml(rawText);

    return (
      escapeHtml(rawText.slice(0, index)) +
      '<strong>' + escapeHtml(rawText.slice(index, index + rawQuery.length)) + '</strong>' +
      escapeHtml(rawText.slice(index + rawQuery.length))
    );
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (res) {
      return res.text().then(function (text) {
        if (!res.ok) {
          var httpErr = new Error('Request failed (' + res.status + ')');
          httpErr.status = res.status;
          httpErr.bodySnippet = text.slice(0, 300);
          throw httpErr;
        }
        try {
          return JSON.parse(text);
        } catch (parseErr) {
          // Response was 200 OK but not valid JSON — most commonly means
          // the request resolved to an HTML page (e.g. an alternate
          // template that wasn't found, so Shopify fell back to the
          // theme's default HTML template) instead of the intended
          // JSON view. bodySnippet makes that immediately visible in
          // the console instead of a generic parse-error message.
          var jsonErr = new Error('Response was not valid JSON (got ' + (res.headers.get('content-type') || 'unknown content-type') + ')');
          jsonErr.bodySnippet = text.slice(0, 300);
          throw jsonErr;
        }
      });
    });
  }

  /* ------------------------------------------------------------ */
  function validateFullSearchResponse(data) {
    if (!data || !Array.isArray(data.products) || typeof data.results_count !== 'number' ||
        typeof data.current_page !== 'number' || typeof data.pages !== 'number' ||
        typeof data.has_next_page !== 'boolean') {
      throw new Error('Full search response did not match the expected JSON contract');
    }

    return data;
  }
  /* Shopify data mapping                                          */
  /* Normalizes three different native Shopify JSON shapes into one */
  /* common product shape the existing card markup already expects */
  /* ------------------------------------------------------------ */

  // Source: /search/suggest.json  resources.results.products[]  (suggestions only)
  function mapPredictiveProduct(p) {
    var price = parseFloat(p.price_min != null ? p.price_min : p.price) || 0;
    var comparePrice = parseFloat(p.compare_at_price_min) || 0;
    return {
      id: p.id,
      title: p.title,
      url: p.url,
      image: normalizeImage(p.image),
      price: price,
      compareAtPrice: comparePrice > price ? comparePrice : null,
      available: p.available !== false
    };
  }

  // Source: templates/search.custom-search.json.liquid  products[]
  // Liquid money fields (item.price / item.compare_at_price) are integer
  // cents, matching how every other Shopify money Liquid object works.
  //
  // __skus starts from whatever the search JSON response provides (if a
  // "skus" field is present -- some theme/template configurations don't
  // reliably surface this custom field, so it's treated as optional here).
  // ensureRealSkus() below fills it in from a guaranteed-native Shopify
  // endpoint for any product this didn't cover, so SKU matching never
  // silently depends on one specific template actually including it.
  function mapFullSearchProduct(p) {
    var price = typeof p.price === 'number' ? p.price / 100 : parseFloat(p.price) || 0;
    var compareAtPrice = null;
    if (p.compare_at_price != null) {
      var cap = typeof p.compare_at_price === 'number' ? p.compare_at_price / 100 : parseFloat(p.compare_at_price);
      if (!isNaN(cap) && cap > price) compareAtPrice = cap;
    }

      return {
        id: p.id,
        title: p.title,
        url: p.url,
        image: normalizeImage(p.image),
        price: price,
        compareAtPrice: compareAtPrice,
        available: p.available !== false,
        __skus: Array.isArray(p.skus) ? p.skus.filter(Boolean) : []
      };
    }

  // Extracts a product handle from a /products/<handle>... URL (query
  // string / tracking params ignored). Used to fetch each candidate's real
  // variant data from Shopify's native per-product endpoint.
  function extractProductHandle(url) {
    var m = (url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // Guarantees every product has real __skus data before SKU matching runs,
  // regardless of whether the search JSON response already included a
  // "skus" field. For any product missing it, fetches
  // /products/<handle>.js -- a core, guaranteed-native Shopify storefront
  // endpoint (used throughout Shopify themes for cart/quick-add, requires
  // no custom template, no alternate view, nothing that can silently be
  // stale or misconfigured) -- and reads the real variant SKUs from there.
  // A single product's lookup failing (network hiccup, 404, etc.) degrades
  // that one product to "no SKU data" rather than failing the search.
  function ensureRealSkus(products, signal) {
    var needsFetch = products.filter(function (p) { return !p.__skus || !p.__skus.length; });
    if (!needsFetch.length) return Promise.resolve(products);

    var fetches = needsFetch.map(function (p) {
      var handle = extractProductHandle(p.url);
      if (!handle) return Promise.resolve();
      var url = shopifyRoot() + 'products/' + handle + '.js';
      return fetchJson(url, { signal: signal, credentials: 'same-origin' })
        .then(function (data) {
          p.__skus = Array.isArray(data.variants)
            ? data.variants.map(function (v) { return v.sku; }).filter(Boolean)
            : [];
        })
        .catch(function () {
          // Leave __skus as [] -- this product simply won't be eligible for
          // an exact/partial SKU match; it never breaks the overall search.
        });
    });

    return Promise.all(fetches).then(function () { return products; });
  }

  // Normalizes a SKU/query for comparison: trims and lowercases only.
  // Deliberately does NOT strip dashes/spaces/etc, because that would
  // turn a prefix/substring match into a false "exact" match (e.g. query
  // "511081" must not be reported as an EXACT match of a real SKU
  // "511081BT" -- that pair is a PARTIAL match, handled separately below).
  function normalizeSkuForCompare(s) {
    return (s == null ? '' : String(s)).trim().toLowerCase();
  }

  // Classifies products against a single-token query using ONLY real
  // variant SKU data (see ensureRealSkus) -- never the query's shape,
  // spelling, or any guess about what "kind" of query it is. A product only
  // ends up in `exact` or `partial` because its actual SKU factually equals
  // or contains the query; nothing here assumes what a SKU "looks like".
  //   exact:   a variant SKU is byte-for-byte equal to the query (case-insensitive)
  //   partial: a variant SKU contains the query as a substring (case-insensitive),
  //            e.g. query "511081" against a real SKU "511081BT"
  function classifySkuMatches(products, query) {
    var q = normalizeSkuForCompare(query);
    var exact = [];
    var partial = [];
    if (!q) return { exact: exact, partial: partial };

    products.forEach(function (p) {
      var skus = p.__skus || [];
      var isExact = skus.some(function (sku) { return normalizeSkuForCompare(sku) === q; });
      if (isExact) {
        exact.push(p);
        return;
      }
      var isPartial = skus.some(function (sku) {
        var normSku = normalizeSkuForCompare(sku);
        return normSku && normSku.indexOf(q) !== -1;
      });
      if (isPartial) partial.push(p);
    });

    return { exact: exact, partial: partial };
  }

  // Source: /collections/all/products.json  products[]
  function mapCatalogProduct(p) {
    var variants = p.variants || [];
    var prices = variants
      .map(function (v) { return parseFloat(v.price); })
      .filter(function (n) { return !isNaN(n); });
    var price = prices.length ? Math.min.apply(null, prices) : 0;

    var compareCandidates = variants
      .map(function (v) { return parseFloat(v.compare_at_price); })
      .filter(function (n) { return !isNaN(n) && n > 0; });
    var compareAtPrice = compareCandidates.length ? Math.max.apply(null, compareCandidates) : null;
    if (compareAtPrice !== null && compareAtPrice <= price) compareAtPrice = null;

    var image = p.images && p.images[0] ? normalizeImage(p.images[0].src) : null;
    var available = typeof p.available === 'boolean'
      ? p.available
      : variants.some(function (v) { return v.available; });

    return {
      id: p.id,
      title: p.title,
      url: '/products/' + p.handle,
      image: image,
      price: price,
      compareAtPrice: compareAtPrice,
      available: available
    };
  }

  function productCardHTML(product, currencyCode) {
    var isSale = !!product.compareAtPrice;
    var isSoldOut = !product.available;

    var badge = '';
    if (isSale) badge += '<span class="cs-card__badge cs-card__badge--sale">Sale</span>';
    if (isSoldOut) badge += '<span class="cs-card__badge cs-card__badge--soldout">Sold Out</span>';

    var priceRow = '';
    if (isSale) {
      priceRow += '<span class="cs-card__price cs-card__price--compare">' + formatMoney(product.compareAtPrice, currencyCode) + '</span>';
    }
    priceRow +=
      '<span class="cs-card__price cs-card__price--current' +
      (isSale ? ' is-sale' : '') +
      (isSoldOut ? ' is-soldout' : '') +
      '">' + formatMoney(product.price, currencyCode) + '</span>';
    priceRow += '<span class="cs-card__currency">' + escapeHtml(currencyCode) + '</span>';

    var availability = isSoldOut
      ? '<span class="cs-card__availability is-out">Out of stock</span>'
      : '';

    var imageSrc = product.image || PLACEHOLDER_IMAGE;

    return (
      '<li class="cs-card">' +
        '<a href="' + escapeHtml(product.url) + '" class="cs-card__link">' +
          '<div class="cs-card__media">' +
            '<img class="cs-card__image" src="' + escapeHtml(imageSrc) + '" alt="' + escapeHtml(product.title) + '" width="300" height="300" loading="lazy">' +
            badge +
          '</div>' +
          '<div class="cs-card__info">' +
            '<h3 class="cs-card__title">' + escapeHtml(product.title) + '</h3>' +
            '<div class="cs-card__prices">' + priceRow + '</div>' +
            availability +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  /* ------------------------------------------------------------ */
  /* localStorage — Latest Searches                                */
  /* ------------------------------------------------------------ */

  function loadLatestSearches() {
    try {
      var raw = window.localStorage.getItem(LATEST_SEARCHES_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(function (t) { return typeof t === 'string' && t.trim(); }) : [];
    } catch (e) {
      return [];
    }
  }

  function saveLatestSearches(list) {
    try {
      window.localStorage.setItem(LATEST_SEARCHES_KEY, JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private mode / quota) — fail silently, UI still works in-memory */
    }
  }

  /* ------------------------------------------------------------ */
  /* CustomSearch controller                                       */
  /* ------------------------------------------------------------ */

  function CustomSearch(panel) {
    this.panel = panel;
   this.overlay = panel.querySelector('#cs-search-overlay') || panel;
this.inner = panel.querySelector('#cs-search-inner') || panel.querySelector('#cs-search-body') || panel;
    this.form = panel.querySelector('#cs-search-form');
    this.input = panel.querySelector('#cs-search-input');
    this.clearBtn = panel.querySelector('#cs-search-clear');
    this.closeBtn = panel.querySelector('#cs-search-close');

    this.emptyState = panel.querySelector('#cs-state-empty');
    this.activeState = panel.querySelector('#cs-state-active');

    this.latestChips = panel.querySelector('#cs-latest-chips');
    this.deleteAllBtn = panel.querySelector('#cs-delete-all');
    this.popularChips = panel.querySelector('#cs-popular-chips');

    this.carouselViewport = panel.querySelector('#cs-carousel-viewport');
    this.carouselTrack = panel.querySelector('#cs-carousel-track');
    this.carouselPrevBtn = panel.querySelector('#cs-carousel-prev');
    this.carouselNextBtn = panel.querySelector('#cs-carousel-next');
    this.recommendedLoadingEl = panel.querySelector('#cs-recommended-loading');
    this.recommendedErrorEl = panel.querySelector('#cs-recommended-error');
    this.recommendedRetryBtn = panel.querySelector('#cs-recommended-retry');

    this.suggestions = panel.querySelector('#cs-suggestions');
    this.searchLoadingEl = panel.querySelector('#cs-search-loading');
    this.searchErrorEl = panel.querySelector('#cs-search-error');
    this.searchRetryBtn = panel.querySelector('#cs-search-retry');
    this.resultsCount = panel.querySelector('#cs-results-count');
    this.resultsGrid = panel.querySelector('#cs-results-grid');
    this.emptyResults = panel.querySelector('#cs-empty-results');
    this.sortSelect = panel.querySelector('#cs-sort-select');
    this.sentinel = panel.querySelector('#cs-results-sentinel');
    this.loadMoreLoadingEl = panel.querySelector('#cs-load-more-loading');

    this.currency = panel.dataset.currency || 'USD';
    this.popularSearches = (panel.dataset.popularSearches || '')
      .split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
    if (!this.popularSearches.length) this.popularSearches = FALLBACK_POPULAR_SEARCHES;

    this.latestSearches = loadLatestSearches();
    this.lastQuery = '';

    // full search / pagination state
    this.searchToken = 0;
    this.fullSearchController = null;
    this.pageController = null;
    this.currentPage = 1;
    this.totalPages = 1;
    this.hasNextPage = false;
    this.pageLoading = false;
    // true when the current result set came from an exact-SKU-scoped
    // query rather than the normal broad text search — see runFullSearch.
    this.skuMode = false;

    // suggestions state
    this.suggestController = null;

    this.debounceTimer = null;
    this.suggestToken = 0;
    this.isOpen = false;
    this.recommendedLoaded = false;
    this.recommendedLoading = false;
    this.lastFocusedElement = null;
    this.savedScrollY = 0;
    this.bodyStyleSnapshot = null;

    this.init();
  }

  CustomSearch.prototype.init = function () {
    this.renderLatestChips();
    this.renderPopularChips();
    this.bindEvents();
    this.updateCarouselButtons();
    this.setupInfiniteScroll();
  };

  CustomSearch.prototype.bindEvents = function () {
    var self = this;

    this.closeBtn.addEventListener('click', function () { self.close(); });
    this.overlay.addEventListener('click', function () { self.close(); });

    this.form.addEventListener('submit', function (e) {
      // Results already render inside the panel as the customer types;
      // Enter commits the search and forces an immediate (non-debounced)
      // refresh instead of navigating away to the native /search page.
      e.preventDefault();
      var query = self.input.value.trim();
      if (!query) return;
      self.runImmediateSearch(query);
    });

    this.input.addEventListener('input', function () { self.handleInput(); });

    this.clearBtn.addEventListener('click', function () {
      self.input.value = '';
      self.input.focus();
      self.handleInput();
    });

    this.deleteAllBtn.addEventListener('click', function () {
      self.latestSearches = [];
      saveLatestSearches(self.latestSearches);
      self.renderLatestChips();
    });

    this.carouselPrevBtn.addEventListener('click', function () { self.scrollCarousel(-1); });
    this.carouselNextBtn.addEventListener('click', function () { self.scrollCarousel(1); });
    this.carouselViewport.addEventListener('scroll', function () { self.updateCarouselButtons(); });
    this.recommendedRetryBtn.addEventListener('click', function () {
      self.recommendedLoaded = false;
      self.loadRecommendedProducts();
    });

    this.sortSelect.addEventListener('change', function () {
      if (self.lastQuery) self.runFullSearch(self.lastQuery, 1);
    });

    this.searchRetryBtn.addEventListener('click', function () {
      if (self.lastQuery) self.runFullSearch(self.lastQuery, 1);
    });

    this.panel.addEventListener('keydown', function (e) { self.handleKeydown(e); });
  };

  /* ---------------------------- open/close ---------------------------- */

  CustomSearch.prototype.open = function () {
    if (this.isOpen) {
      this.input.focus();
      return;
    }

    this.isOpen = true;
    this.lastFocusedElement = document.activeElement;

    // Capture the exact page position before locking the document.
    this.savedScrollY = window.pageYOffset || window.scrollY || document.documentElement.scrollTop || 0;
    this.bodyStyleSnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight
    };

    // Fixed-body locking prevents the underlying Shopify page from moving
    // while the search panel itself remains independently scrollable.
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this.savedScrollY + 'px';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Avoid a layout shift when the scrollbar disappears.
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }

    // Re-opening always starts the search panel at the top.
    this.inner.scrollTop = 0;

    this.panel.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cs-scroll-lock');

    this.loadRecommendedProducts();

    var self = this;
    window.requestAnimationFrame(function () {
      if (self.isOpen) self.input.focus();
    });
  };

  CustomSearch.prototype.close = function () {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');

    // Abort in-flight UI requests when the panel is closed. This does not
    // change search logic; it simply prevents hidden-panel work from racing
    // against a later open.
    if (this.suggestController) {
      this.suggestController.abort();
      this.suggestController = null;
    }

    document.documentElement.classList.remove('cs-scroll-lock');

    var snapshot = this.bodyStyleSnapshot || {};
    document.body.style.position = snapshot.position || '';
    document.body.style.top = snapshot.top || '';
    document.body.style.width = snapshot.width || '';
    document.body.style.overflow = snapshot.overflow || '';
    document.body.style.paddingRight = snapshot.paddingRight || '';

    // Restore the exact page position after unlocking the body.
    var savedY = typeof this.savedScrollY === 'number' ? this.savedScrollY : 0;
    window.scrollTo(0, savedY);

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      try {
        this.lastFocusedElement.focus({ preventScroll: true });
      } catch (e) {
        this.lastFocusedElement.focus();
      }
    }
  };

  CustomSearch.prototype.handleKeydown = function (e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  CustomSearch.prototype.trapFocus = function (e) {
    var focusable = this.panel.querySelectorAll(
      'button:not([hidden]):not([disabled]), input:not([hidden]), select:not([hidden]), a[href]'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------- latest searches ---------------------------- */

  CustomSearch.prototype.commitSearch = function (term) {
    term = (term || '').trim();
    if (!term) return;
    var list = this.latestSearches.filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    list = list.slice(0, LATEST_SEARCHES_MAX);
    this.latestSearches = list;
    saveLatestSearches(list);
    this.renderLatestChips();
  };

  CustomSearch.prototype.runImmediateSearch = function (term) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.input.value = term;
    this.clearBtn.hidden = false;
    this.commitSearch(term);
    this.showActiveState();
    this.runSuggestions(term);
    this.runFullSearch(term, 1);
  };

  /* ---------------------------- chips ---------------------------- */

  CustomSearch.prototype.renderLatestChips = function () {
    var self = this;
    this.latestChips.innerHTML = '';
    this.latestChips.classList.toggle('cs-chips--empty', this.latestSearches.length === 0);

    this.latestSearches.forEach(function (term, index) {
      var li = document.createElement('li');
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cs-chip';
      chip.dataset.term = term;

      var label = document.createElement('span');
      label.textContent = term;
      chip.appendChild(label);

      var remove = document.createElement('span');
      remove.className = 'cs-chip__remove';
      remove.setAttribute('role', 'img');
      remove.setAttribute('aria-hidden', 'true');
      remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      chip.appendChild(remove);
      chip.setAttribute('aria-label', 'Search "' + term + '", or press to remove');

      chip.addEventListener('click', function (evt) {
        var rect = remove.getBoundingClientRect();
        var clickedRemove =
          evt.clientX >= rect.left && evt.clientX <= rect.right &&
          evt.clientY >= rect.top && evt.clientY <= rect.bottom;

        if (clickedRemove) {
          self.latestSearches.splice(index, 1);
          saveLatestSearches(self.latestSearches);
          self.renderLatestChips();
        } else {
          self.runImmediateSearch(term);
        }
      });

      li.appendChild(chip);
      self.latestChips.appendChild(li);
    });
  };

  CustomSearch.prototype.renderPopularChips = function () {
    var self = this;
    this.popularChips.innerHTML = '';

    this.popularSearches.forEach(function (term) {
      var li = document.createElement('li');
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cs-chip';
      chip.textContent = term;
      chip.addEventListener('click', function () { self.runImmediateSearch(term); });
      li.appendChild(chip);
      self.popularChips.appendChild(li);
    });
  };

  /* ---------------------------- carousel ---------------------------- */

  CustomSearch.prototype.scrollCarousel = function (direction) {
    var amount = this.carouselViewport.clientWidth * 0.8 * direction;
    this.carouselViewport.scrollBy({ left: amount, behavior: 'smooth' });
  };

  CustomSearch.prototype.updateCarouselButtons = function () {
    var viewport = this.carouselViewport;
    var maxScroll = viewport.scrollWidth - viewport.clientWidth - 1;
    this.carouselPrevBtn.disabled = viewport.scrollLeft <= 0;
    this.carouselNextBtn.disabled = viewport.scrollLeft >= maxScroll;
  };

  /* ---------------------------- recommended products ---------------------------- */
  /*
   * Shopify's native contextual recommendations endpoint
   * (/recommendations/products.json) requires a product_id and only
   * makes sense on a product page. The search panel has no such
   * context, so there is no native "generic recommendations" API.
   * Best native fallback (no invented API, no hardcoded list): the
   * automatic /collections/all listing, native, always current.
   */

  CustomSearch.prototype.loadRecommendedProducts = function () {
    if (this.recommendedLoaded || this.recommendedLoading) return;
    this.recommendedLoading = true;
    this.setRecommendedLoading(true);
    this.setRecommendedError(false);

    var self = this;
    var url = shopifyRoot() + 'collections/all/products.json?sort_by=best-selling&limit=' + RECOMMENDED_LIMIT;

    fetchJson(url, { credentials: 'same-origin' })
      .then(function (data) {
        var products = (data.products || []).map(mapCatalogProduct);
        self.renderRecommended(products);
        self.recommendedLoaded = true;
        self.recommendedLoading = false;
        self.setRecommendedLoading(false);
      })
      .catch(function () {
        self.recommendedLoading = false;
        self.setRecommendedLoading(false);
        self.setRecommendedError(true);
      });
  };

  CustomSearch.prototype.renderRecommended = function (products) {
    var self = this;
    this.carouselTrack.innerHTML = products.map(function (p) { return productCardHTML(p, self.currency); }).join('');
    this.updateCarouselButtons();
  };

  CustomSearch.prototype.setRecommendedLoading = function (isLoading) {
    this.recommendedLoadingEl.hidden = !isLoading;
    this.carouselTrack.classList.toggle('is-loading', isLoading);
  };

  CustomSearch.prototype.setRecommendedError = function (hasError) {
    this.recommendedErrorEl.hidden = !hasError;
  };

  /* ---------------------------- search state ---------------------------- */

  CustomSearch.prototype.handleInput = function () {
    var query = this.input.value.trim();
    this.clearBtn.hidden = query.length === 0;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (query.length === 0) {
      this.cancelPendingSearches();
      this.setSearchLoading(false);
      this.setSearchError(false);
      this.showEmptyState();
      return;
    }

    this.showActiveState();
    var self = this;
    this.debounceTimer = setTimeout(function () {
      self.runSuggestions(query);
      self.runFullSearch(query, 1);
    }, DEBOUNCE_MS);
  };

  CustomSearch.prototype.cancelPendingSearches = function () {
    this.suggestToken += 1;
    if (this.suggestController) this.suggestController.abort();
    if (this.fullSearchController) this.fullSearchController.abort();
    if (this.pageController) this.pageController.abort();
    this.searchToken += 1;
  };

  CustomSearch.prototype.showEmptyState = function () {
    this.emptyState.hidden = false;
    this.activeState.hidden = true;
  };

  CustomSearch.prototype.showActiveState = function () {
    this.emptyState.hidden = true;
    this.activeState.hidden = false;
  };

  /* ---------------------------- predictive search (suggestions only) ---------------------------- */

  CustomSearch.prototype.buildSuggestUrl = function (query) {
    return shopifyRoot() + 'search/suggest.json' +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product,query' +
      '&resources[limit]=' + SUGGESTION_LIMIT +
      '&resources[options][unavailable_products]=show' +
      // variants.sku / variants.barcode added so a SKU/barcode query can
      // surface a matching product as a suggestion pill too (previously
      // absent, so a query like "403030" produced zero suggestions even
      // though it's a real, valid SKU). Title/product_type/vendor/tag
      // matching for normal keyword typeahead is unchanged.
      '&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag';
  };

  CustomSearch.prototype.runSuggestions = function (query) {
    if (this.suggestController) this.suggestController.abort();

    var requestToken = ++this.suggestToken;
    var controller = new AbortController();
    this.suggestController = controller;

    var self = this;
    fetchJson(this.buildSuggestUrl(query), { signal: controller.signal, credentials: 'same-origin' })
      .then(function (data) {
        if (requestToken !== self.suggestToken || !self.isOpen) return;

        var results = (data && data.resources && data.resources.results) || {};
        var rawQueries = results.queries || [];
        var rawProducts = results.products || [];
        self.renderSuggestions(rawQueries, rawProducts.map(mapPredictiveProduct), query);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Suggestions are a nice-to-have; fail silently rather than
        // showing an error banner for the whole panel over a typeahead miss.
        self.suggestions.hidden = true;
        self.suggestions.innerHTML = '';
      });
  };

  CustomSearch.prototype.renderSuggestions = function (rawQueries, products, query) {
    var terms = rawQueries.map(function (q) { return q.text; }).filter(Boolean);

    if (!terms.length) {
      var seen = {};
      products.forEach(function (p) {
        var key = p.title.toLowerCase();
        if (!seen[key]) {
          seen[key] = true;
          terms.push(p.title);
        }
      });
    }

    terms = terms.slice(0, SUGGESTION_LIMIT);

    if (!terms.length) {
      this.suggestions.hidden = true;
      this.suggestions.innerHTML = '';
      return;
    }

    var self = this;
    this.suggestions.hidden = false;
    this.suggestions.innerHTML = terms
      .map(function (text) {
        return '<li><button type="button" class="cs-suggestions__item" data-term="' + escapeHtml(text) + '">' + highlightMatch(text, query) + '</button></li>';
      })
      .join('');

    this.suggestions.querySelectorAll('.cs-suggestions__item').forEach(function (btn) {
      btn.addEventListener('click', function () { self.runImmediateSearch(btn.dataset.term); });
    });
  };

  /* ---------------------------- full search (results grid + pagination) ---------------------------- */

  CustomSearch.prototype.buildFullSearchUrl = function (query, page) {
    var url = shopifyRoot() + 'search' +
      '?q=' + encodeURIComponent(query) +
      '&type=product' +
      '&view=' + SEARCH_VIEW +
      '&page=' + page;
    var sortBy = SORT_MAP[this.sortSelect.value];
    if (sortBy) url += '&sort_by=' + sortBy;
    // Cache-busting: forces Shopify's edge cache to always be bypassed for
    // this endpoint. Without this, a URL that ever returned a bad/error
    // response (e.g. during theme deployment) could keep being served
    // from cache to later identical requests, producing exactly the
    // "same query, inconsistent result" symptom this endpoint must avoid.
    url += '&_=' + Date.now();
    return url;
  };

  CustomSearch.prototype.runFullSearch = function (query, page, isRetry) {
    var isAppend = page > 1;

    if (!isAppend) {
      if (this.fullSearchController) this.fullSearchController.abort();
      if (this.pageController) this.pageController.abort();
      // Reset immediately (not just on response) so a scroll that happens
      // in the gap before this new query's page-1 response arrives can't
      // trigger a page-2 fetch for it and race the initial request.
      this.hasNextPage = false;
      this.pageLoading = false;
      this.setLoadMoreLoading(false);
      var controller = new AbortController();
      this.fullSearchController = controller;
    } else {
      if (this.pageController) this.pageController.abort();
      var pageCtl = new AbortController();
      this.pageController = pageCtl;
    }

    var token = isAppend ? this.searchToken : (this.searchToken += 1);
    this.lastQuery = query;

    if (isAppend) {
      this.pageLoading = true;
      this.setLoadMoreLoading(true);
    } else if (!isRetry) {
      this.currentPage = 1;
      this.setSearchLoading(true);
      this.setSearchError(false);
    }

    var self = this;
    var signal = isAppend ? this.pageController.signal : this.fullSearchController.signal;
    // Always the normal broad Shopify search -- this is the one and only
    // data source for candidates, for every query type (SKU, title, vendor,
    // keyword...). Shopify's "sku:" query-scoping syntax was tried and found
    // unreliable (fuzzy/substring matching, not exact equality). SKU
    // relevance is computed below, client-side, against each candidate's
    // REAL variant SKUs -- fetched via ensureRealSkus() from Shopify's
    // guaranteed-native /products/<handle>.js endpoint, not from any
    // specific alternate search template -- so this never silently breaks
    // if a particular template configuration doesn't surface SKU data.
    // Title, vendor, and keyword matching are entirely untouched, still
    // decided by Shopify's own broad search exactly as before this fix.
    var url = this.buildFullSearchUrl(query, page);

    fetchJson(url, { signal: signal, credentials: 'same-origin' })
      .then(function (data) {
        data = validateFullSearchResponse(data);
        if (token !== self.searchToken) return; // a newer query has since started

        var products = (data.products || []).map(mapFullSearchProduct);
        var resultsCount = data.results_count || 0;
        var trimmedQuery = (query || '').trim();
        // A SKU is always a single token (no spaces); a multi-word phrase
        // (e.g. "12 in 1", "chlorine tablets") is never itself a SKU, so it
        // always keeps using Shopify's normal broad results untouched.
        // "Load more" (isAppend) never re-runs SKU matching -- it only
        // continues the broad text results, matching whatever page 1
        // already decided (skuMode disables "load more" entirely below).
        var attemptSkuMatch = !isAppend && !!trimmedQuery && !/\s/.test(trimmedQuery);

        function finish(finalProducts, finalCount, skuMode) {
          if (token !== self.searchToken) return; // a newer query started while SKUs were being fetched
          if (!isAppend) self.skuMode = skuMode;
          self.currentPage = data.current_page || page;
          self.totalPages = data.pages || 1;
          self.hasNextPage = self.skuMode ? false : (!!data.has_next_page && self.currentPage < self.totalPages);
          if (isAppend) {
            self.appendResults(finalProducts);
            self.pageLoading = false;
            self.setLoadMoreLoading(false);
          } else {
            self.setSearchLoading(false);
            self.renderFirstPage(finalProducts, finalCount, query);
          }
        }

        if (!attemptSkuMatch) {
          finish(products, resultsCount, false);
          return;
        }

        // Fetch each candidate's real variant SKUs (bounded by
        // SKU_CHECK_CANDIDATE_LIMIT -- see its definition), then classify.
        // Exact real-SKU matches take priority over everything else -- this
        // is what makes an exact SKU search (e.g. "403030") show only that
        // product instead of it being buried among unrelated text matches.
        var skuCheckCandidates = products.slice(0, SKU_CHECK_CANDIDATE_LIMIT);
        return ensureRealSkus(skuCheckCandidates, signal).then(function () {
          var skuMatches = classifySkuMatches(skuCheckCandidates, trimmedQuery);
          if (skuMatches.exact.length) {
            finish(skuMatches.exact, skuMatches.exact.length, true);
          } else if (skuMatches.partial.length) {
            // No exact match, but the query is a real substring of at least
            // one product's actual SKU (e.g. "511081" inside real SKU
            // "511081BT") -- a genuine partial-SKU match, factually grounded
            // in real SKU data fetched above, not a guess.
            finish(skuMatches.partial, skuMatches.partial.length, true);
          } else {
            // Neither exact nor partial: this query has no real relationship
            // to any candidate's SKU at all, so it's left completely alone --
            // Shopify's normal broad results (title/vendor/tag/keyword
            // matches) are shown exactly as returned. This is what keeps a
            // query like a model number that only appears in a product's
            // TITLE (not its SKU field) working correctly, instead of being
            // incorrectly hidden by a "looks like a SKU" guess.
            finish(products, resultsCount, false);
          }
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') {
          if (isAppend) {
            self.pageLoading = false;
            self.setLoadMoreLoading(false);
          }
          return;
        }
        if (token !== self.searchToken) return;

        // One transparent retry before surfacing an error to the customer.
        // This masks transient blips (momentary network failure, a stale
        // edge-cache entry, a one-off Liquid render hiccup) without ever
        // showing "Something went wrong" for something that resolves on
        // a second attempt a moment later.
        if (!isAppend && !isRetry) {
          console.warn('[custom-search] full search request failed, retrying once — url:', url, 'error:', err && err.message);
          window.setTimeout(function () {
            if (token === self.searchToken) self.runFullSearch(query, page, true);
          }, 400);
          return;
        }

        console.error(
          '[custom-search] full search request failed' + (isRetry ? ' (after retry)' : '') + ' — url:', url,
          '| status:', err && err.status,
          '| message:', err && err.message,
          '| response body (first 300 chars):', err && err.bodySnippet
        );

        if (isAppend) {
          self.pageLoading = false;
          self.setLoadMoreLoading(false);
        } else {
          self.setSearchLoading(false);
          self.setSearchError(true);
        }
      });
  };

  CustomSearch.prototype.renderFirstPage = function (products, resultsCount, query) {
    var self = this;
    var safeQuery = escapeHtml(query);

    if (resultsCount === 0) {
      this.resultsCount.innerHTML = '0 results for &ldquo;' + safeQuery + '&rdquo;';
      this.resultsGrid.innerHTML = '';
      this.emptyResults.hidden = false;
      return;
    }

    this.resultsCount.innerHTML = '<strong>' + resultsCount + '</strong> result' + (resultsCount === 1 ? '' : 's') + ' for &ldquo;' + safeQuery + '&rdquo;';
    this.emptyResults.hidden = true;
    this.resultsGrid.innerHTML = products.map(function (p) { return productCardHTML(p, self.currency); }).join('');
  };

  CustomSearch.prototype.appendResults = function (products) {
    var self = this;
    if (!products.length) return;
    var html = products.map(function (p) { return productCardHTML(p, self.currency); }).join('');
    this.resultsGrid.insertAdjacentHTML('beforeend', html);
  };

  CustomSearch.prototype.setSearchLoading = function (isLoading) {
    this.searchLoadingEl.hidden = !isLoading;
    this.resultsGrid.classList.toggle('is-loading', isLoading);
  };

  CustomSearch.prototype.setSearchError = function (hasError) {
    this.searchErrorEl.hidden = !hasError;
    if (hasError) {
      this.resultsGrid.innerHTML = '';
      this.emptyResults.hidden = true;
      this.resultsCount.innerHTML = '';
      this.hasNextPage = false;
    }
  };

  CustomSearch.prototype.setLoadMoreLoading = function (isLoading) {
    this.loadMoreLoadingEl.hidden = !isLoading;
  };

  /* ---------------------------- infinite scroll ---------------------------- */

  CustomSearch.prototype.setupInfiniteScroll = function () {
    if (typeof IntersectionObserver !== 'function' || !this.sentinel) return;
    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) self.maybeLoadNextPage();
      },
      { root: this.inner, rootMargin: '400px' }
    );
    this.observer.observe(this.sentinel);
  };

  CustomSearch.prototype.maybeLoadNextPage = function () {
    if (!this.lastQuery || !this.hasNextPage || this.pageLoading) return;
    if (this.activeState.hidden) return;
    this.runFullSearch(this.lastQuery, this.currentPage + 1);
  };

  /* ------------------------------------------------------------ */
  /* Bootstrap                                                     */
  /* ------------------------------------------------------------ */

function initCustomSearch() {
  var panel = document.getElementById('cs-search-panel');

  if (!panel) {
    console.error('[Custom Search] #cs-search-panel not found');
    return;
  }

  if (window.CustomSearchInstance) {
    return;
  }

  window.CustomSearchInstance = new CustomSearch(panel);

  console.log('[Custom Search] Initialized successfully');
}

/* ------------------------------------------------------------ */
/* Header search trigger connection                              */
/* --------------------------------------------------------------
   Wires the real Shopify header search triggers (desktop bar +
   mobile icon) to the existing window.CustomSearchInstance panel.
   Does not touch header.liquid, search API logic, category
   filtering, ranking, or the panel's own markup/styling.
   ------------------------------------------------------------ */

function openCustomSearchFromHeader() {
  if (
    window.CustomSearchInstance &&
    typeof window.CustomSearchInstance.open === 'function'
  ) {
    window.CustomSearchInstance.open();
    return true;
  }
  return false;
}

// CustomSearchInstance may not exist yet at click time (e.g. this script's
// own init hasn't run, or #cs-search-panel loaded late) — retry briefly
// instead of failing silently.
function waitAndOpenCustomSearchFromHeader(retries) {
  if (openCustomSearchFromHeader()) return;
  if (retries > 0) {
    setTimeout(function () {
      waitAndOpenCustomSearchFromHeader(retries - 1);
    }, 100);
  }
}

function installCustomSearchHeaderTriggers() {
  if (window.__customSearchHeaderTriggersInstalled) return;
  window.__customSearchHeaderTriggersInstalled = true;

  // DESKTOP — anywhere inside .cstm-search-bar (input, icon, empty space)
  // NOTE: matched WITHOUT a ".header__search-area" ancestor requirement.
  // The live DOM confirmed the form as
  //   <form action="/search" method="get" class="cstm-search-bar">
  // with no ".header__search-area" wrapper around it, so the earlier
  // ".header__search-area .search-btn" / ".header__search-area
  // .cstm-search-bar" selectors never matched -- closest() returned null,
  // the handlers silently no-opped, and the native /search submission
  // went through uninterrupted. ".cstm-search-bar" is unique to this form
  // elsewhere in the theme, so matching on it directly is safe.
  // The whole bar is a click target (not just .search-btn) so clicking the
  // input or empty space opens the panel too. ".search-category" (the
  // native collection <select>) is explicitly excluded so it keeps
  // opening/behaving as a normal dropdown instead of being hijacked.
  document.addEventListener('click', function (event) {
    var bar = event.target && event.target.closest
      ? event.target.closest('.cstm-search-bar')
      : null;
    if (!bar) return;
    if (event.target.closest('.search-category')) return;
    event.preventDefault();
    waitAndOpenCustomSearchFromHeader(20);
  }, true);

  // DESKTOP — mousedown on .cstm-search-bar, excluding .search-category.
  // preventDefault() on 'click' alone is too late to stop the native input
  // from taking browser focus, since focus is assigned on mousedown before
  // the click event fires. Blocking it here stops that flash of native
  // focus so only the custom panel's own input ends up focused once it
  // opens (see CustomSearch.prototype.open's rAF-scheduled focus call).
  document.addEventListener('mousedown', function (event) {
    var bar = event.target && event.target.closest
      ? event.target.closest('.cstm-search-bar')
      : null;
    if (!bar) return;
    if (event.target.closest('.search-category')) return;
    event.preventDefault();
  }, true);

  // DESKTOP — Enter key inside .cstm-search-bar (excluding the category select)
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var form = event.target && event.target.closest
      ? event.target.closest('.cstm-search-bar')
      : null;
    if (!form) return;
    if (event.target.closest('.search-category')) return;
    event.preventDefault();
    waitAndOpenCustomSearchFromHeader(20);
  }, true);

  // DESKTOP — form submit (catch-all safety net)
  // Fires for every path that leads to /search navigation (button click,
  // Enter, a virtual keyboard's "Go"/"Search" action, or any other script
  // calling form.submit()/requestSubmit()), independent of exactly which
  // element was interacted with. Belt-and-suspenders alongside the click
  // and keydown handlers above; window.CustomSearchInstance.open() is
  // idempotent so multiple handlers firing for one user action is harmless.
  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.closest
      ? event.target.closest('.cstm-search-bar')
      : null;
    if (!form) return;
    event.preventDefault();
    waitAndOpenCustomSearchFromHeader(20);
  }, true);

  // MOBILE — .header__mobile-search-btn[data-mobile-search-open]
  // Bubble phase (not capture): header.liquid's own inline script may
  // already have a capture-phase listener on this exact button that
  // opens the panel itself and stops propagation. When that's present,
  // this delegated listener simply never sees the click — the desired
  // cooperative behavior, no duplicate open() call. When it's absent
  // (or doesn't stop propagation), this is the one that opens the panel.
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest
      ? event.target.closest('.header__mobile-search-btn[data-mobile-search-open]')
      : null;
    if (!btn) return;
    event.preventDefault();
    waitAndOpenCustomSearchFromHeader(20);
  }, false);
}

// Installed immediately (not gated behind initCustomSearch/panel lookup)
// so the header triggers work regardless of whether CustomSearchInstance
// initializes before or after this runs — document-level delegation
// needs no target element to exist yet, only document itself.
installCustomSearchHeaderTriggers();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomSearch);
} else {
  initCustomSearch();
}
})();
/* =========================================================
   MOBILE HEADER SEARCH → CUSTOM SEARCH PANEL
   ========================================================= */

(function () {
  'use strict';

  function openCustomSearch() {
    if (
      window.CustomSearchInstance &&
      typeof window.CustomSearchInstance.open === 'function'
    ) {
      window.CustomSearchInstance.open();
      return true;
    }

    return false;
  }

  function tryOpenCustomSearch(attempts) {
    if (openCustomSearch()) return;

    if (attempts > 0) {
      setTimeout(function () {
        tryOpenCustomSearch(attempts - 1);
      }, 150);
    }
  }

  // Intercept mobile search BEFORE the theme's native search drawer
  document.addEventListener(
    'click',
    function (event) {
      if (window.innerWidth > 991) return;

      var button = event.target.closest(
        '.header__mobile-search-btn[data-mobile-search-open]'
      );

      if (!button) return;

      // Stop Shopify/Impact native search
      event.preventDefault();
      event.stopPropagation();

      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }

      // Close native search drawer if already active
      var nativeDrawer = document.querySelector(
        '#search-drawer, search-drawer, [data-search-drawer]'
      );

      if (nativeDrawer) {
        nativeDrawer.removeAttribute('open');
        nativeDrawer.classList.remove(
          'is-open',
          'open',
          'active'
        );
      }

      // Open our custom search
      tryOpenCustomSearch(30);
    },
    true
  );

})();